/**
 * Module dependencies.
 */

import { ProductThickness } from 'src/types/products';
import { availableFiltersKeys } from 'src/core/collections-config/filters';
import { units } from 'src/styles/utils';
import { useFiltersList } from 'src/context/filters-list/context';
import { useTranslate } from 'src/core/utils/translator';
import FilterDisclosure from 'src/components/filters/filter-disclosure';
import FilterItem from 'src/components/filters/filter-item';
import FilterItemLabel from 'src/components/filters/filter-item-label';
import Loading from 'src/components/core/loading';
import React, { ReactElement, useMemo } from 'react';
import Type from 'src/components/core/typography/index';
import filter from 'lodash/filter';
import get from 'lodash/get';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import keys from 'lodash/keys';
import map from 'lodash/map';
import size from 'lodash/size';
import split from 'lodash/split';
import styled from 'styled-components';
import useProductThicknesses from 'src/api/app/products/use-product-thicknesses';

/**
 * Filter name.
 */

const filterName = availableFiltersKeys.thickness;

/**
 * `LoadingWrapper` styled component.
 */

const LoadingWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 150px;
`;

/**
 * `StyledFilterItem` styled component.
 */

const StyledFilterItem = styled(FilterItem)`
  padding: ${units(3)} 0 ${units(3)} ${units(2)} !important;
`;

/**
 * `Props` type.
 */

type Props = {
  filtersList?: ProductThickness[];
};

/**
 * `ThicknessFilter` component.
 */

const ThicknessFilter = ({ filtersList }: Props): ReactElement => {
  const { translate } = useTranslate();
  const { data, isLoading, isSuccess } = useProductThicknesses({ enabled: isEmpty(filtersList) });
  const thicknessList = useMemo(
    () => (isEmpty(filtersList) ? (data ?? []) : filtersList),
    [data, filtersList]
  );

  const hasThicknesses = size(thicknessList) > 0;

  const { filters } = useFiltersList();
  const selectedFilters = useMemo(() => {
    const hasFilter = keys(filters).includes(filterName);

    return hasFilter
      ? split(filters[filterName], ',').map((id: string) => {
          const name = get(head(filter(thicknessList, { id })), 'name');

          return `${name}mm`;
        })
      : [];
  }, [filters, thicknessList]);

  return (
    <FilterDisclosure name={filterName} selectedFilters={selectedFilters} title={translate('common:labels.thickness')}>
      {isEmpty(filtersList) && isLoading && (
        <LoadingWrapper>
          <Loading active />
        </LoadingWrapper>
      )}

      {isEmpty(filtersList) && isSuccess && !hasThicknesses && (
        <Type.Paragrap paddingVertical={units(1)}>{translate('collections:list.filters.emptyThickness')}</Type.Paragrap>
      )}

      {hasThicknesses &&
        map(thicknessList, ({ id, name }: ProductThickness) => (
          <StyledFilterItem key={id} label={<FilterItemLabel title={`${name}mm`} />} name={filterName} value={id} />
        ))}
    </FilterDisclosure>
  );
};

/**
 * Export `ThicknessFilter` component.
 */

export default ThicknessFilter;
